import { SemanasDiasFieldModel } from 'components/form/field/SemanasDiasField'
import {
  ExameEspecificoEnum,
  FichaComplementarZikaMicrocefaliaResultadoExameEnum,
  OpcoesResultadoExameQualitativoEnum,
  ResultadoExameEspecializadoInput,
  ResultadoExameEspecifico,
  TipoExameEnum,
} from 'graphql/types.generated'
import { MetaPath } from 'util/metaPath'
import { ErrorObject, range, required, validate } from 'util/validation'
import { SoapEditableItem } from 'view/atendimentos/atendimento-individual/model'

import { SolicitacaoExameCitopatologicoQueryModel } from '../../plano/exames/components/requisicao-exame/citopatologico/model-citopatologico'
import { ResultadoExameMarcadoresBioquimicosDecimalField } from './adicionar/exames-especificos/ResultadoExameMarcadoresBioquimicosDecimalField'
import { ResultadoExamePrenatalField } from './adicionar/exames-especificos/ResultadoExamePrenatalField'
import { ResultadoExamePuericulturaField } from './adicionar/exames-especificos/ResultadoExamePuericulturaField'
import { ResultadoExameQualitativoField } from './adicionar/exames-especificos/ResultadoExameQualitativoField'
import {
  validateResultadoExamePrenatal,
  validateResultadoExamePuericultura,
  validateResultadoExameQualitativo,
} from './adicionar/validator-ResultadosExamesObjetivo'
import { formatResultadoExameEspecificoDecimal } from './util-resultadosExames'
import {
  convertResultadoExameMarcadoresBioquimicosToInput,
  convertResultadoExameMarcadoresBioquimicosValueToModel,
  convertResultadoExamePrenatalToInput,
  convertResultadoExamePrenatalValueToModel,
  convertResultadoExamePuericulturaToInput,
  convertResultadoExamePuericulturaValueToModel,
  convertResultadoExameQualitativoToInput,
  convertResultadoExameQualitativoValueToModel,
} from './visualizar/exames-especificos/converter-ExamesEspecificos'
import {
  extractResultadoExameMarcadoresBioquimicosValue,
  extractResultadoExamePrenatalValue,
  extractResultadoExamePuericulturaValue,
  extractResultadoExameQualitativoValue,
} from './visualizar/exames-especificos/extract-resultadoExame'
import { ResultadoExameMarcadoresBioquimicosView } from './visualizar/exames-especificos/ResultadoExameMarcadoresBioquimicosView'
import { ResultadoExamePrenatalView } from './visualizar/exames-especificos/ResultadoExamePrenatalView'
import { ResultadoExamePuericulturaView } from './visualizar/exames-especificos/ResultadoExamePuericulturaView'
import { ResultadoExameQualitativoView } from './visualizar/exames-especificos/ResultadoExameQualitativoView'

type AtendimentoProfissional = {
  id: ID
  iniciadoEm?: number
  lotacao?: {
    id: ID
    cbo: {
      id: ID
      nome: string
    }
    unidadeSaude: {
      id: ID
      nome?: string
    }
    profissional: {
      id: ID
      nome: string
    }
  }
}

export type CodigosExamesEspecificos =
  | '0202010503'
  | '0202010295'
  | '0202010279'
  | '0202010287'
  | '0202010678'
  | '0202010317'
  | '0202050025'
  | '0205020143'
  | '0205010059'
  | '0211070149'
  | '0211070270'
  | '0205020178'
  | '0206010079'
  | '0207010064'
  | '0211060100'
  | 'ABEX022'
  | '0205020151'
  | '0202020509'

export type ResultadoEspecificoPrimeiroBloco = string

export type ResultadoEspecificoPrenatal = {
  idadeGestacional: SemanasDiasFieldModel
  dpp: LocalDate
  error?: string
}

export type ResultadoEspecificoPuericultura = FichaComplementarZikaMicrocefaliaResultadoExameEnum

export type ResultadoEspecificoQualitativo = OpcoesResultadoExameQualitativoEnum

export type AllResultadoEspecificoValueType = ResultadoEspecificoPrimeiroBloco &
  ResultadoEspecificoPrenatal &
  ResultadoEspecificoPuericultura &
  ResultadoEspecificoQualitativo

export type ExameQueryModel = {
  id: ID
  codigo: string
  descricao: string
  procedimentoReferencia?: { id: ID; descricao: string; codigo: string }
  idadeMaxima?: number
  idadeMinima?: number
  idadeAplicavel?: boolean
}

export type ResultadoExameQueryModel = {
  id: ID
  exame: ExameQueryModel
  solicitacao?: { id: ID; data: LocalDate; atendimentoProfissional?: AtendimentoProfissional }
  conferido?: boolean
  dataSolicitacao?: LocalDate
  dataRealizacao?: LocalDate
  dataResultado?: LocalDate
  especifico?: ResultadoExameEspecifico
  resultado?: string
  atendimentoProfissionalResultado?: AtendimentoProfissional
}

export type SolicitacaoExameQueryModel = {
  id: ID
  data: LocalDate
  exames: ReadonlyArray<ResultadoExameQueryModel>
  cid10?: {
    id: ID
    codigo: string
    nome: string
    sexo?: string
  }
  justificativa: string
  observacoes?: string
  tipoExame: TipoExameEnum

  atendimentoProfissional: AtendimentoProfissional
  solicitacaoCitopatologico?: SolicitacaoExameCitopatologicoQueryModel | null
}

export interface ResultadoExameComSolicitacaoModel extends SoapEditableItem {
  resultado?: string
  dataRealizado?: string
  dataResultado?: string
  exame?: ExameQueryModel
}

export interface ResultadoExameSemSolicitacaoModel extends SoapEditableItem {
  exame: ExameQueryModel
  resultado?: string
  dataRealizado?: string
  dataResultado?: string
  dataSolicitado?: string
  createdAt: Instant
}

export interface ResultadoExameEspecificoModel extends SoapEditableItem {
  resultado?: AllResultadoEspecificoValueType
  descricao?: string
  dataRealizado?: string
  dataResultado?: string
  dataSolicitado?: string
  exame?: ExameQueryModel
  createdAt?: Instant
}

export type ResultadosExamesSolicitadosModel = Record<
  ID,
  ResultadoExameComSolicitacaoModel & ResultadoExameEspecificoModel
>

export type ReplicateDatesModel = {
  dataRealizado: LocalDate
  dataResultado: LocalDate
}

export type ResultadosExamesNaoSolicitadosModel = Record<
  ID,
  ResultadoExameSemSolicitacaoModel & ResultadoExameEspecificoModel
>

export type ResultadosExamesModel = {
  resultadosComSolicitacao?: ResultadosExamesSolicitadosModel
  resultadosSemSolicitacao?: ResultadosExamesNaoSolicitadosModel
  replicateComSolicitacao?: Record<ID, ReplicateDatesModel>
  replicateSemSolicitacao?: ReplicateDatesModel
}

export type FactoryResultadoExameEspecificoModel = {
  validator(props: FactoryResultadoExameEspecificoValidatorProps): string | ErrorObject<AllResultadoEspecificoValueType>
  converterToInput(value: ResultadoExameEspecificoModel): ResultadoExameEspecializadoInput
  extractValueFromQuery(resultadoExame: ResultadoExameQueryModel, minified?: boolean): string | JSX.Element
  converterValueToModel(input: ResultadoExameEspecifico, exame: ExameQueryModel): AllResultadoEspecificoValueType
  formatOnSave?(value: ResultadoExameEspecificoModel, exame: ExameQueryModel): ResultadoExameEspecificoModel
  components: {
    ResultadoField: React.ComponentType<FactoryResultadoExameEspecificoFieldProps>
    ResultadoView: React.ComponentType<FactoryResultadoExameEspecificoViewProps>
  }
  properties: FactoryResultadoExameEspecificoProperties
}

export type FactoryResultadoExameEspecificoProperties = {
  relatedDbEnum: ExameEspecificoEnum
  unit?: string
  maxLength?: number
  precision?: number
  options?: (FichaComplementarZikaMicrocefaliaResultadoExameEnum | OpcoesResultadoExameQualitativoEnum)[]
}

export type FactoryResultadoExameEspecificoFieldProps = {
  name: MetaPath<AllResultadoEspecificoValueType>
  exame: ExameQueryModel
  expanded: boolean
  dataSolicitacao?: Date
  dataRealizado?: Date
}

export type FactoryResultadoExameEspecificoViewProps = {
  value: AllResultadoEspecificoValueType
  exame: ExameQueryModel
}

export type FactoryResultadoExameEspecificoValidatorProps = {
  value: AllResultadoEspecificoValueType
  args?: {
    dataRealizado: LocalDate
    dataSolicitado: LocalDate
    dataNascimento: LocalDate
  }
}

export type ResultadosExamesEspecificosFactory = Record<CodigosExamesEspecificos, FactoryResultadoExameEspecificoModel>

export const resultadosExamesEspecificosFactory = (): ResultadosExamesEspecificosFactory => ({
  '0202010503': {
    validator: ({ value }) => validate(value, [required, range(0, 100)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 6,
      precision: 2,
      unit: '%',
      relatedDbEnum: ExameEspecificoEnum.HEMOGLOBINA_GLICADA,
    },
  },
  '0202010295': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 2,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_TOTAL,
    },
  },
  '0202010279': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 2,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_HDL,
    },
  },
  '0202010287': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 2,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.COLESTEROL_LDL,
    },
  },
  '0202010678': {
    validator: ({ value }) => validate(value, [required, range(1, 10000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 2,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.TRIGLICERIDIOS,
    },
  },
  '0202010317': {
    validator: ({ value }) => validate(value, [required, range(0.1, 500)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 6,
      precision: 2,
      unit: 'mg/dL',
      relatedDbEnum: ExameEspecificoEnum.DOSAGEM_DE_CREATINA,
    },
  },
  '0202050025': {
    validator: ({ value }) => validate(value, [required, range(0.001, 1000)]),
    converterToInput: (value) => convertResultadoExameMarcadoresBioquimicosToInput(value),
    converterValueToModel: (input, exame) => convertResultadoExameMarcadoresBioquimicosValueToModel(input, exame),
    extractValueFromQuery: extractResultadoExameMarcadoresBioquimicosValue,
    formatOnSave: (value, exame) => formatResultadoExameEspecificoDecimal(value, exame),
    components: {
      ResultadoField: ResultadoExameMarcadoresBioquimicosDecimalField,
      ResultadoView: ResultadoExameMarcadoresBioquimicosView,
    },
    properties: {
      maxLength: 8,
      precision: 3,
      unit: 'mL/min/1,73 m²',
      relatedDbEnum: ExameEspecificoEnum.CLEARANCE_DE_CREATINA,
    },
  },
  '0205020143': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0205020151': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0205010059': {
    validator: validateResultadoExamePrenatal,
    converterToInput: (value) => convertResultadoExamePrenatalToInput(value),
    converterValueToModel: convertResultadoExamePrenatalValueToModel,
    extractValueFromQuery: extractResultadoExamePrenatalValue,
    components: {
      ResultadoField: ResultadoExamePrenatalField,
      ResultadoView: ResultadoExamePrenatalView,
    },
    properties: {
      relatedDbEnum: ExameEspecificoEnum.PRENATAL,
    },
  },
  '0211070149': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_FALHOU,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_PASSOU,
      ],
      relatedDbEnum: ExameEspecificoEnum.PEATEAB,
    },
  },
  '0211070270': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_FALHOU,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.ORELHINHA_PASSOU,
      ],
      relatedDbEnum: ExameEspecificoEnum.PEATEST,
    },
  },
  '0205020178': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TRANSFONTANELA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_US,
    },
  },
  '0206010079': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.TOMOGRAFIA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_TC,
    },
  },
  '0207010064': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_SUGESTIVO_INFECCAO_CONGENITA,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_OUTRAS_ALTERACOES,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.RESSONANCIA_INDETERMINADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.EXAMES_IMAGEM_RMN,
    },
  },
  '0211060100': {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.FUNDO_OLHO_NORMAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.FUNDO_OLHO_ALTERADO,
      ],
      relatedDbEnum: ExameEspecificoEnum.FUNDOSCOPIA,
    },
  },
  ABEX022: {
    validator: validateResultadoExamePuericultura,
    converterToInput: (value) => convertResultadoExamePuericulturaToInput(value),
    converterValueToModel: convertResultadoExamePuericulturaValueToModel,
    extractValueFromQuery: extractResultadoExamePuericulturaValue,
    components: {
      ResultadoField: ResultadoExamePuericulturaField,
      ResultadoView: ResultadoExamePuericulturaView,
    },
    properties: {
      options: [
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.PRESENTE_BILATERAL,
        FichaComplementarZikaMicrocefaliaResultadoExameEnum.DUVIDOSO_OU_AUSENTE,
      ],
      relatedDbEnum: ExameEspecificoEnum.TESTE_OLHINHO,
    },
  },
  '0202020509': {
    validator: validateResultadoExameQualitativo,
    converterToInput: (value) => convertResultadoExameQualitativoToInput(value),
    converterValueToModel: convertResultadoExameQualitativoValueToModel,
    extractValueFromQuery: extractResultadoExameQualitativoValue,
    components: {
      ResultadoField: ResultadoExameQualitativoField,
      ResultadoView: ResultadoExameQualitativoView,
    },
    properties: {
      options: [
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_POSITIVO,
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_NEGATIVO,
        OpcoesResultadoExameQualitativoEnum.PROVA_LACO_INCONCLUSIVO,
      ],
      relatedDbEnum: ExameEspecificoEnum.PROVA_DO_LACO,
    },
  },
})

export const examesEspecificosKeys = Object.keys(resultadosExamesEspecificosFactory())
